import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../lib/adminApi";

export default function AdminReset() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Admin Password Reset | Vaskion Technologies";
  }, []);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await adminApi.post("/auth/forgot-password", { email });
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 10) {
      setError("Please choose a password of at least 10 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await adminApi.post("/auth/reset-password", { email, token, newPassword });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-surface px-4">
      <div className="w-full max-w-md border border-border bg-white p-8 shadow-sm sm:p-10">
        <div className="flex items-center gap-3">
          <KeyRound className="h-8 w-8 text-brand-red" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-brand-graphite">Password Setup / Reset</h1>
            <p className="text-sm text-brand-grey">Admin Dashboard — Vaskion Technologies</p>
          </div>
        </div>

        {done ? (
          <div className="mt-8" data-testid="admin-reset-success">
            <CheckCircle2 className="h-9 w-9 text-brand-red" aria-hidden="true" />
            <p className="mt-4 text-base text-brand-graphite">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link to="/admin" className="btn-primary mt-6 inline-block" data-testid="admin-reset-goto-login">
              Go to Sign In
            </Link>
          </div>
        ) : step === 1 ? (
          <form onSubmit={requestCode} className="mt-8 space-y-5" data-testid="admin-reset-request-form">
            <p className="text-sm leading-relaxed text-brand-grey">
              Enter the administrator email address. A one-time reset code will be emailed to it.
            </p>
            <div>
              <label htmlFor="reset-email" className="field-label">Administrator Email</label>
              <input
                id="reset-email"
                type="email"
                required
                className="field-input"
                placeholder="admin@vaskiontech.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-reset-email-input"
              />
            </div>
            {error && (
              <p className="border-l-2 border-brand-red bg-brand-surface p-3 text-sm text-brand-graphite" role="alert" data-testid="admin-reset-error">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="admin-reset-request-button">
              {loading ? "Sending…" : "Email Me a Reset Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="mt-8 space-y-5" data-testid="admin-reset-form">
            <p className="border-l-2 border-brand-red bg-brand-surface p-3 text-sm leading-relaxed text-brand-graphite" data-testid="admin-reset-code-sent-notice">
              If this is the administrator email, a reset code has been sent to it. Paste the code
              below and choose a new password.
            </p>
            <div>
              <label htmlFor="reset-token" className="field-label">Reset Code</label>
              <input
                id="reset-token"
                type="text"
                required
                className="field-input"
                placeholder="Paste the code from the email"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid="admin-reset-token-input"
              />
            </div>
            <div>
              <label htmlFor="reset-new-password" className="field-label">New Password (min. 10 characters)</label>
              <input
                id="reset-new-password"
                type="password"
                required
                autoComplete="new-password"
                className="field-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="admin-reset-new-password-input"
              />
            </div>
            <div>
              <label htmlFor="reset-confirm" className="field-label">Confirm New Password</label>
              <input
                id="reset-confirm"
                type="password"
                required
                autoComplete="new-password"
                className="field-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="admin-reset-confirm-input"
              />
            </div>
            {error && (
              <p className="border-l-2 border-brand-red bg-brand-surface p-3 text-sm text-brand-graphite" role="alert" data-testid="admin-reset-error">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="admin-reset-submit-button">
              {loading ? "Updating…" : "Set New Password"}
            </button>
          </form>
        )}

        {!done && (
          <p className="mt-6 text-center text-sm text-brand-grey">
            <Link to="/admin" className="font-medium text-brand-red underline underline-offset-2" data-testid="admin-reset-back-to-login">
              Back to Sign In
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
